import { useEffect, useRef } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import TableRox from 'src/components/table-rox/TableRox';
import type { LpEventCloneDraft } from 'src/types/lp-events';
import { cloneDay, clonePeriodIsValid } from '../lp-event-clone.utils';

type Props = {
  draft: LpEventCloneDraft;
  startDate: Date | null;
  endDate: Date | null;
  canAutoScroll: boolean;
};

export default function CloneBenefitsPreview({ draft, startDate, endDate, canAutoScroll }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const lastAnchoredPeriod = useRef('');
  const start = cloneDay(startDate);
  const end = cloneDay(endDate);
  const validPeriod = clonePeriodIsValid(startDate, endDate);
  const datesChanged = validPeriod && start !== cloneDay(draft.event.startDate) && end !== cloneDay(draft.event.endDate);

  useEffect(() => {
    const period = `${start}/${end}`;
    if (!canAutoScroll || !datesChanged || lastAnchoredPeriod.current === period) return undefined;
    // Let the date picker finish closing before moving the viewport.
    const timer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      lastAnchoredPeriod.current = period;
    }, 350);
    return () => window.clearTimeout(timer);
  }, [canAutoScroll, datesChanged, start, end]);

  const formatDay = (day: string) => day.split('-').reverse().join('/');
  const rows = [
    ...draft.products.map((item) => ({ ...item, type: 'Produto' })),
    ...draft.coupons.map((item) => ({ ...item, type: 'Cupom' })),
  ].map((item) => ({
    ...item,
    start: formatDay(validPeriod ? start : cloneDay(item.startDate)),
    end: formatDay(validPeriod ? end : cloneDay(item.endDate)),
    statusLabel: item.status === 'active' ? 'Ativo' : 'Inativo',
    expirationLabel: item.expiration?.expirationType === 'date'
      ? formatDay(validPeriod ? end : cloneDay(item.expiration.expirationDate))
      : `${item.expiration?.expirationDays ?? '—'} dias após o resgate`,
  }));

  return (
    <Box ref={sectionRef} sx={{ scrollMarginTop: 100 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Benefícios Associados ao Evento</Typography>
        <Alert severity={datesChanged ? 'success' : 'info'} role="status">
          {validPeriod && (start !== cloneDay(draft.event.startDate) || end !== cloneDay(draft.event.endDate))
            ? `A vigência de todos os benefícios e banners desta cópia foi atualizada para ${formatDay(start)} a ${formatDay(end)}. As alterações serão gravadas ao salvar o evento.`
            : 'Defina as novas datas do evento para atualizar automaticamente a vigência de todos os benefícios e banners da cópia.'}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          {draft.products.length} produtos, {draft.coupons.length} cupons e {draft.banners.length} banners incluídos, com seus status e configurações. A validade por data fixa acompanhará o término do evento; a validade em dias será mantida.
        </Typography>
        <TableRox
          data={rows}
          header={[
            { id: 'name', label: 'Benefício' },
            { id: 'type', label: 'Tipo' },
            { id: 'start', label: 'Início' },
            { id: 'end', label: 'Término' },
            { id: 'expirationLabel', label: 'Validade de uso' },
            { id: 'statusLabel', label: 'Status' },
          ]}
          defaultOrderBy="name"
          selectKey="_id"
          hasSearch
          defaultRowsPerPage={10}
        />
      </Stack>
    </Box>
  );
}
